import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import { FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb, Toast } from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import api from '../../services/api';
import { talentStudentsApi, talentSpecializationsApi, talentStudentSpecializationLinksApi } from '../../services/apiService';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import { createShadow } from '../../utils/styles';

type RouteProps = RouteProp<MainStackParamList, 'EditStudent'>;
type NavProp = NativeStackNavigationProp<MainStackParamList>;

const FOREGROUND = '#414042';
const BORDER = '#E2E8F0';
const SECTION_BG = '#F1F5F9';
const MUTED = '#64748B';
const BRAND_RED = '#A9211F';

/* -- Chevron Down Icon --------------------------------------------------- */
function ChevronDownIcon({ size = 16, color = MUTED }: { size?: number; color?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M6 9l6 6 6-6" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

/* -- Calendar Icon ------------------------------------------------------- */
function CalendarIcon({ size = 20, color = MUTED }: { size?: number; color?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

/* -- X Icon for chip remove ---------------------------------------------- */
function XIcon({ size = 12, color = FOREGROUND }: { size?: number; color?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M18 6L6 18M6 6l12 12" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

/* -- Form Field Component ------------------------------------------------ */
function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <View style={fieldStyles.container}>
      <Text style={fieldStyles.label}>
        {required && <Text style={{ color: '#D62728' }}>*</Text>}
        {` ${label}`}
      </Text>
      {children}
    </View>
  );
}

/* -- Main Screen --------------------------------------------------------- */
export default function EditStudentScreen() {
  const route = useRoute<RouteProps>();
  const navigation = useNavigation<NavProp>();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const { isMobile } = useResponsive();
  const containerPadding = isMobile ? 12 : 20;
  const { user } = useAuth();
  const isOrgEmployee = user?.profile?.user_type === 'org_employee';

  const studentId = route.params?.id;
  const specializationIdParam = route.params?.specializationId;
  const isCreateMode = !studentId;

  // Form state
  const [name, setName] = useState('');
  const [eid, setEid] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [gender, setGender] = useState('');
  const [registrationDate, setRegistrationDate] = useState('');
  const [school, setSchool] = useState('');
  const [ageGroup, setAgeGroup] = useState('');
  const [level, setLevel] = useState('');
  const [status, setStatus] = useState('');
  const [specializationFkId, setSpecializationFkId] = useState<number | null>(specializationIdParam || null);
  const [specializationFkName, setSpecializationFkName] = useState('');
  const [enrolledSpecs, setEnrolledSpecs] = useState<{ id: number; name: string }[]>([]);
  const [allEnrolledSpecs, setAllEnrolledSpecs] = useState<{ id: number; name: string }[]>([]);
  const [photoName, setPhotoName] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);

  // Dropdown states
  const [showGenderDropdown, setShowGenderDropdown] = useState(false);
  const [showAgeGroupDropdown, setShowAgeGroupDropdown] = useState(false);
  const [showLevelDropdown, setShowLevelDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showSpecDropdown, setShowSpecDropdown] = useState(false);
  const [showSchoolDropdown, setShowSchoolDropdown] = useState(false);
  const [specOptions, setSpecOptions] = useState<{ id: number; name: string }[]>([]);
  const [schoolOptions, setSchoolOptions] = useState<{ value: string; label: string }[]>([]);

  const [saving, setSaving] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('info');
  const [isLoading, setIsLoading] = useState(true);

  // Load student data (edit mode only)
  useEffect(() => {
    if (isCreateMode) {
      setIsLoading(false);
      return;
    }
    (async () => {
      try {
        const resp = await talentStudentsApi.get(studentId);
        if (resp) {
          setName(resp.name || '');
          const rawEid = (resp.eid || '').replace(/[^0-9]/g, '');
          let fmtEid = rawEid;
          if (rawEid.length > 3) fmtEid = rawEid.slice(0, 3) + '-' + rawEid.slice(3);
          if (rawEid.length > 7) fmtEid = rawEid.slice(0, 3) + '-' + rawEid.slice(3, 7) + '-' + rawEid.slice(7);
          if (rawEid.length > 14) fmtEid = rawEid.slice(0, 3) + '-' + rawEid.slice(3, 7) + '-' + rawEid.slice(7, 14) + '-' + rawEid.slice(14);
          setEid(fmtEid);
          setEmail(resp.email || '');
          setPhone(resp.phone || '');
          setBirthDate(resp.birth_date || '');
          setGender(resp.gender || '');
          setRegistrationDate(resp.registration_date || '');
          setSchool(resp.school || '');
          setAgeGroup(resp.age_group || '');
          setLevel(resp.level || '');
          // Don't auto-fill status — user selects if they want to change it
          setSpecializationFkId(resp.specialization_fk || null);
          setSpecializationFkName(resp.specialization_name || resp.specialization || '');
          if (resp.photo) setPhotoName(resp.photo.split('/').pop() || '');

          // Load all enrolled specializations from links (for dropdown options)
          try {
            const linksResp = await talentStudentSpecializationLinksApi.list({ student: studentId });
            const links = linksResp?.results || linksResp || [];
            const specsResp = await talentSpecializationsApi.list({ page_size: 200 });
            const allSpecs = specsResp?.results || specsResp || [];
            const specMap = new Map(allSpecs.map((s: any) => [s.id, s.name]));
            const enrolled = links
              .filter((l: any) => l.specialization && specMap.has(l.specialization))
              .map((l: any) => ({ id: l.specialization, name: specMap.get(l.specialization)! }));
            const known: { id: number; name: string }[] = enrolled.length > 0
              ? enrolled
              : (resp.specialization_fk
                ? [{ id: resp.specialization_fk, name: resp.specialization_name || resp.specialization || '' }]
                : []);
            setAllEnrolledSpecs(known);
            // Pre-select what the student is already enrolled in, so editing other
            // fields doesn't force the user to pick the talent again. When opened
            // from a specialization page, scope the selection to that talent.
            const preselected = specializationIdParam
              ? known.filter(s => s.id === Number(specializationIdParam))
              : known;
            if (preselected.length > 0) setEnrolledSpecs(preselected);
          } catch { /* fallback to primary FK already set */ }
        }
      } catch (e) {
        console.log('[EditStudent] load failed:', e);
      }
      setIsLoading(false);
    })();
  }, [studentId, isCreateMode]);

  // Load specialization options + resolve name from param
  useEffect(() => {
    (async () => {
      try {
        const resp = await talentSpecializationsApi.list();
        const items = resp?.results || resp || [];
        const mapped = items.map((s: any) => ({ id: s.id, name: s.name }));
        setSpecOptions(mapped);
        // Resolve name from param if not yet set
        if (specializationIdParam) {
          const match = mapped.find((s: any) => s.id === Number(specializationIdParam));
          if (match) {
            if (!specializationFkName) setSpecializationFkName(match.name);
            // The talent is fixed by the route (field is locked) — show it selected
            setEnrolledSpecs(prev => (
              prev.some(s => s.id === match.id) ? prev : [{ id: match.id, name: match.name }]
            ));
          }
        }
      } catch (e) {
        console.log('[EditStudent] load specs failed:', e);
      }
      try {
        const schools = await talentStudentsApi.schools();
        setSchoolOptions(schools);
      } catch (e) {
        console.log('[EditStudent] load schools failed:', e);
      }
    })();
  }, []);

  const handleSave = async () => {
    if (!name.trim()) {
      setToastType('error');
      setToastMsg(isRTL ? 'يرجى إدخال الاسم' : 'Please enter a name');
      return;
    }
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setToastType('error');
      setToastMsg(isRTL ? 'يرجى إدخال بريد إلكتروني صحيح' : 'Please enter a valid email');
      return;
    }
    const rawPhone = phone.replace(/\s+/g, '');
    if (rawPhone && !/^(\+971|971|0)?[0-9]{8,9}$/.test(rawPhone)) {
      setToastType('error');
      setToastMsg(isRTL ? 'يرجى إدخال رقم هاتف صحيح (مثال: 05X XXX XXXX)' : 'Please enter a valid UAE phone number');
      return;
    }
    const eidDigits = eid.replace(/[^0-9]/g, '');
    if (eidDigits && eidDigits.length !== 15) {
      setToastType('error');
      setToastMsg(isRTL ? `رقم الهوية يجب أن يكون 15 رقم (الحالي: ${eidDigits.length})` : `Emirates ID must be 15 digits (current: ${eidDigits.length})`);
      return;
    }
    // A status change needs a target talent. Leaving the status empty simply means
    // "don't touch the statuses" — the pre-selected talents stay as they are.
    if (status && enrolledSpecs.length === 0) {
      setToastType('error');
      setToastMsg(isRTL ? 'يرجى اختيار الموهبة لتطبيق الحالة عليها' : 'Please select talent(s) to apply the status to');
      return;
    }
    setSaving(true);
    try {
      const payload: any = {
        name: name.trim(),
        eid: eid.replace(/[^0-9]/g, ''),
        email: email.trim(),
        phone: phone.trim(),
        birth_date: birthDate || null,
        gender: gender || '',
        registration_date: registrationDate || null,
        school: school.trim(),
        specialization_fk: specializationFkId,
        age_group: ageGroup || '',
        level: level || '',
      };
      // specialization_ids only scopes a status change on the backend, so send the
      // pair together — never a status-less list that would be ignored anyway.
      if (status) {
        payload.status = status;
        if (enrolledSpecs.length > 0) {
          payload.specialization_ids = enrolledSpecs.map(s => s.id);
        }
      }
      let savedStudentId = studentId;
      if (isCreateMode) {
        const created = await talentStudentsApi.create(payload);
        savedStudentId = created.id;
        setToastType('success');
        setToastMsg(isRTL ? 'تم إضافة الطالب بنجاح' : 'Student added successfully');
      } else {
        await talentStudentsApi.update(studentId, payload);
        setToastType('success');
        setToastMsg(isRTL ? 'تم تحديث البيانات بنجاح' : 'Data updated successfully');
      }
      // Upload photo if selected
      if (photoFile && savedStudentId) {
        const formData = new FormData();
        formData.append('photo', photoFile);
        await api.patch(`/talent-students/${savedStudentId}/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      setTimeout(() => navigation.goBack(), 800);
    } catch (e) {
      console.log('[EditStudent] save failed:', e);
      setToastType('error');
      setToastMsg(isRTL
        ? (isCreateMode ? 'فشل في إضافة الطالب' : 'فشل في تحديث البيانات')
        : (isCreateMode ? 'Failed to add student' : 'Failed to update data'));
    }
    setSaving(false);
  };

  const genderOptions = [
    { value: 'male', label: isRTL ? '\u0630\u0643\u0631' : 'Male' },
    { value: 'female', label: isRTL ? '\u0623\u0646\u062B\u0649' : 'Female' },
  ];

  const ageGroupOptions = [
    { value: 'foundation', label: isRTL ? '\u0627\u0644\u0645\u0631\u062D\u0644\u0629 \u0627\u0644\u062A\u0623\u0633\u064A\u0633\u064A\u0629' : 'Foundation' },
    { value: 'intermediate', label: isRTL ? '\u0627\u0644\u0645\u0631\u062D\u0644\u0629 \u0627\u0644\u0645\u062A\u0648\u0633\u0637\u0629' : 'Intermediate' },
    { value: 'secondary', label: isRTL ? '\u0627\u0644\u0645\u0631\u062D\u0644\u0629 \u0627\u0644\u062B\u0627\u0646\u0648\u064A\u0629' : 'Secondary' },
  ];

  const levelOptions = [
    { value: 'emerging', label: isRTL ? '\u0627\u0644\u0641\u0626\u0629 \u0627\u0644\u0646\u0627\u0634\u0626\u0629' : 'Emerging' },
    { value: 'promising', label: isRTL ? '\u0627\u0644\u0641\u0626\u0629 \u0627\u0644\u0648\u0627\u0639\u062F\u0629' : 'Promising' },
    { value: 'distinguished', label: isRTL ? '\u0627\u0644\u0641\u0626\u0629 \u0627\u0644\u0645\u062A\u0645\u064A\u0632\u0629' : 'Distinguished' },
    { value: 'representative', label: isRTL ? '\u0627\u0644\u0641\u0626\u0629 \u0627\u0644\u0642\u0627\u0628\u0644\u0629 \u0644\u0644\u062A\u0645\u062B\u064A\u0644 \u0627\u0644\u062E\u0627\u0631\u062C\u064A' : 'Representative' },
  ];

  const statusOptions = isOrgEmployee ? [
    { value: 'active', label: isRTL ? 'معتمد' : 'Approved' },
    { value: 'rejected', label: isRTL ? 'مرفوض' : 'Rejected' },
    { value: 'withdrawn', label: isRTL ? 'منسحب' : 'Withdrawn' },
    { value: 'inactive', label: isRTL ? 'غير فعال' : 'Inactive' },
  ] : [
    { value: 'active', label: isRTL ? 'معتمد' : 'Approved' },
    { value: 'proposed', label: isRTL ? 'مقترح' : 'Proposed' },
    { value: 'applied', label: isRTL ? 'متقدم بطلب' : 'Applied' },
    { value: 'withdrawn', label: isRTL ? 'منسحب' : 'Withdrawn' },
  ];

  const genderLabel = genderOptions.find(g => g.value === gender)?.label || '';
  const ageGroupLabel = ageGroupOptions.find(a => a.value === ageGroup)?.label || '';
  const levelLabel = levelOptions.find(l => l.value === level)?.label || '';
  const statusLabel = statusOptions.find(s => s.value === status)?.label || '';
  const schoolLabel = schoolOptions.find(s => s.value === school)?.label || '';

  if (isLoading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Text style={{ fontFamily: FONTS.regular, fontSize: 14, color: MUTED }}>
            {isRTL ? '\u062C\u0627\u0631\u064A \u0627\u0644\u062A\u062D\u0645\u064A\u0644...' : 'Loading...'}
          </Text>
        </View>
      </PageContainer>
    );
  }

  /* Helper to render a dropdown field */
  const renderDropdown = (
    label: string,
    value: string,
    displayLabel: string,
    placeholder: string,
    options: { value: string; label: string }[],
    isOpen: boolean,
    setIsOpen: (v: boolean) => void,
    setValue: (v: string) => void,
    isRequired: boolean = true,
  ) => (
    <FormField label={label} required={isRequired}>
      <TouchableOpacity
        style={[inputStyles.container, { flexDirection: 'row', alignItems: 'center' }]}
        onPress={() => setIsOpen(!isOpen)}
      >
        <ChevronDownIcon size={20} color={MUTED} />
        <Text style={[inputStyles.input, { flex: 1 }]}>
          {displayLabel || placeholder}
        </Text>
      </TouchableOpacity>
      {isOpen && (
        <View style={[dropdownStyles.menu, options.length > 10 && { maxHeight: 250 }]}>
          <ScrollView nestedScrollEnabled>
            {options.map((opt) => (
              <TouchableOpacity
                key={opt.value}
                style={[
                  dropdownStyles.item,
                  value === opt.value && { backgroundColor: '#F1F5F9' },
                ]}
                onPress={() => {
                  setValue(opt.value);
                  setIsOpen(false);
                }}
              >
                <Text style={dropdownStyles.itemText}>{opt.label}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      )}
    </FormField>
  );

  return (
    <PageContainer>
      <Navbar />
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ flexGrow: 1 }}>
        <View style={{ padding: containerPadding, gap: 20 }}>
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: isRTL ? 'الرئيسية' : 'Home' },
              ...(isCreateMode
                ? [{ label: isRTL ? 'مبادرة مواهب' : 'Talents' }]
                : [{ label: isRTL ? 'تفاصيل الطالب' : 'Student Details' }]),
              { label: isRTL
                ? (isCreateMode ? 'إضافة طالب' : 'تعديل البيانات')
                : (isCreateMode ? 'Add Student' : 'Edit Data') },
            ]}
          />

          {/* Main Card */}
          <View style={[cardStyles.container, createShadow(0, 1, 3, '#000', 0.1)]}>
            <Text style={cardStyles.title}>
              {isRTL
                ? (isCreateMode ? 'إضافة طالب جديد' : 'تعديل بيانات الطالب')
                : (isCreateMode ? 'Add New Student' : 'Edit Student Data')}
            </Text>

            {/* Section 1: Personal Information */}
            <View style={sectionStyles.container}>
              <Text style={sectionStyles.title}>
                {isRTL ? 'المعلومات الشخصية' : 'Personal Information'}
              </Text>

              {/* Photo upload */}
              <FormField label={isRTL ? 'الصورة الشخصية' : 'Profile Photo'}>
                <View style={[inputStyles.container, { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end' }]}>
                  <Text style={{ fontFamily: FONTS.regular, fontSize: 14, color: photoName ? FOREGROUND : MUTED, flex: 1, textAlign: 'right' }}>
                    {photoName || (isRTL ? 'لم يتم اختيار ملف' : 'No file chosen')}
                  </Text>
                  <TouchableOpacity
                    style={{ borderWidth: 1, borderColor: BORDER, borderRadius: 4, paddingHorizontal: 8, paddingVertical: 4, marginLeft: 8 }}
                    onPress={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/png,image/jpeg';
                      input.onchange = (e: any) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setPhotoFile(file);
                          setPhotoName(file.name);
                        }
                      };
                      input.click();
                    }}
                  >
                    <Text style={{ fontFamily: FONTS.regular, fontSize: 12, color: FOREGROUND }}>
                      {isRTL ? 'اختر ملف' : 'Choose file'}
                    </Text>
                  </TouchableOpacity>
                </View>
                <Text style={{ fontSize: 11, fontFamily: FONTS.regular, color: MUTED, textAlign: 'right', marginTop: 2 }}>
                  {isRTL ? 'الصورة الشخصية يجب أن تكون بمقاس بين 256*256 و 1024*1024. تقبل صيغة PNG أو JPEG' : 'Photo must be between 256x256 and 1024x1024. PNG or JPEG format accepted'}
                </Text>
              </FormField>

              {/* Row 1: Name + Birth Date */}
              <View style={sectionStyles.row}>
                <FormField label={isRTL ? 'تاريخ الميلاد' : 'Date of Birth'} required>
                  <View style={inputStyles.container}>
                    {Platform.OS === 'web' ? (
                      <input
                        type="date"
                        value={birthDate}
                        onChange={(e: any) => setBirthDate(e.target.value)}
                        style={{
                          flex: 1, border: 'none', outline: 'none',
                          backgroundColor: 'transparent', fontSize: 14,
                          fontFamily: FONTS.regular, color: FOREGROUND,
                          textAlign: isRTL ? 'right' : 'left',
                          direction: isRTL ? 'rtl' : 'ltr',
                          padding: '0', cursor: 'pointer', minHeight: 21,
                        } as any}
                      />
                    ) : (
                      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <CalendarIcon size={20} color={MUTED} />
                        <TextInput
                          style={[inputStyles.input, { flex: 1, marginLeft: 8 }]}
                          value={birthDate} onChangeText={setBirthDate}
                          placeholder="YYYY-MM-DD" placeholderTextColor="#94A3B8"
                          textAlign={isRTL ? 'right' : 'left'}
                        />
                      </View>
                    )}
                  </View>
                </FormField>
                <FormField label={isRTL ? 'الاسم' : 'Name'} required>
                  <View style={inputStyles.container}>
                    <TextInput style={inputStyles.input} value={name} onChangeText={setName}
                      placeholder={isRTL ? 'أدخل الاسم' : 'Enter name'} placeholderTextColor="#94A3B8" textAlign={isRTL ? 'right' : 'left'} />
                  </View>
                </FormField>
              </View>

              {/* Row 1b: EID */}
              <View style={sectionStyles.row}>
                <FormField label={isRTL ? 'رقم الهوية (EID)' : 'Emirates ID (EID)'}>
                  {Platform.OS === 'web' ? (
                    <div dir="ltr" style={{
                      backgroundColor: '#FFFFFF', border: `1px solid ${BORDER}`, borderRadius: 8,
                      paddingLeft: 12, paddingRight: 12, paddingTop: 7, paddingBottom: 7,
                      minHeight: 36, boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    }}>
                      <input
                        type="text"
                        dir="ltr"
                        ref={(el: any) => { if (el) el.style.setProperty('direction', 'ltr', 'important'); }}
                        value={eid}
                        onChange={(e: any) => {
                          const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 15);
                          let formatted = digits;
                          if (digits.length > 3) formatted = digits.slice(0, 3) + '-' + digits.slice(3);
                          if (digits.length > 7) formatted = digits.slice(0, 3) + '-' + digits.slice(3, 7) + '-' + digits.slice(7);
                          if (digits.length > 14) formatted = digits.slice(0, 3) + '-' + digits.slice(3, 7) + '-' + digits.slice(7, 14) + '-' + digits.slice(14);
                          setEid(formatted);
                        }}
                        placeholder="784-XXXX-XXXXXXX-X"
                        maxLength={18}
                        style={{
                          width: '100%', border: 'none', outline: 'none',
                          backgroundColor: 'transparent', fontSize: 14,
                          fontFamily: FONTS.regular, color: FOREGROUND,
                          textAlign: 'left',
                          padding: '0', minHeight: 21,
                        } as any}
                      />
                    </div>
                  ) : (
                    <View style={inputStyles.container}>
                      <TextInput style={[inputStyles.input, { writingDirection: 'ltr', textAlign: 'left' }]} value={eid}
                        onChangeText={(text) => {
                          const digits = text.replace(/[^0-9]/g, '').slice(0, 15);
                          let formatted = digits;
                          if (digits.length > 3) formatted = digits.slice(0, 3) + '-' + digits.slice(3);
                          if (digits.length > 7) formatted = digits.slice(0, 3) + '-' + digits.slice(3, 7) + '-' + digits.slice(7);
                          if (digits.length > 14) formatted = digits.slice(0, 3) + '-' + digits.slice(3, 7) + '-' + digits.slice(7, 14) + '-' + digits.slice(14);
                          setEid(formatted);
                        }}
                        placeholder="784-XXXX-XXXXXXX-X" placeholderTextColor="#94A3B8"
                        keyboardType="number-pad"
                      />
                    </View>
                  )}
                </FormField>
              </View>

              {/* Row 2: Email + Phone */}
              <View style={sectionStyles.row}>
                <FormField label={isRTL ? 'الهاتف' : 'Phone'} required>
                  <View style={[inputStyles.container, { flexDirection: 'row', alignItems: 'center' }]}>
                    <TextInput
                      style={[inputStyles.input, { flex: 1 }]}
                      value={phone}
                      onChangeText={(text) => {
                        const digits = text.replace(/[^0-9]/g, '');
                        setPhone(digits);
                      }}
                      placeholder={isRTL ? '5X XXX XXXX' : '5X XXX XXXX'}
                      placeholderTextColor="#94A3B8"
                      textAlign={isRTL ? 'right' : 'left'}
                      keyboardType="phone-pad"
                    />
                    <View style={{ borderLeftWidth: 1, borderLeftColor: BORDER, paddingLeft: 8, paddingRight: 4, flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                      <Text style={{ fontFamily: FONTS.regular, fontSize: 14, color: FOREGROUND }}>971+</Text>
                    </View>
                  </View>
                </FormField>
                <FormField label={isRTL ? 'البريد الالكتروني' : 'Email'} required>
                  <View style={inputStyles.container}>
                    <TextInput
                      style={inputStyles.input}
                      value={email}
                      onChangeText={setEmail}
                      placeholder={isRTL ? 'example@domain.com' : 'example@domain.com'}
                      placeholderTextColor="#94A3B8"
                      textAlign={isRTL ? 'right' : 'left'}
                      keyboardType="email-address"
                      autoCapitalize="none"
                    />
                  </View>
                </FormField>
              </View>

              {/* Row 3: Gender + Registration Date */}
              <View style={sectionStyles.row}>
                <FormField label={isRTL ? 'تاريخ التسجيل' : 'Registration Date'} required>
                  <View style={inputStyles.container}>
                    {Platform.OS === 'web' ? (
                      <input
                        type="date"
                        value={registrationDate}
                        onChange={(e: any) => setRegistrationDate(e.target.value)}
                        style={{
                          flex: 1, border: 'none', outline: 'none',
                          backgroundColor: 'transparent', fontSize: 14,
                          fontFamily: FONTS.regular, color: FOREGROUND,
                          textAlign: isRTL ? 'right' : 'left',
                          direction: isRTL ? 'rtl' : 'ltr',
                          padding: '0', cursor: 'pointer', minHeight: 21,
                        } as any}
                      />
                    ) : (
                      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <CalendarIcon size={20} color={MUTED} />
                        <TextInput
                          style={[inputStyles.input, { flex: 1, marginLeft: 8 }]}
                          value={registrationDate} onChangeText={setRegistrationDate}
                          placeholder="YYYY-MM-DD" placeholderTextColor="#94A3B8"
                          textAlign={isRTL ? 'right' : 'left'}
                        />
                      </View>
                    )}
                  </View>
                </FormField>
                {renderDropdown(
                  isRTL ? 'الجنس' : 'Gender', gender, genderLabel,
                  isRTL ? 'اختر الجنس' : 'Select gender',
                  genderOptions, showGenderDropdown, setShowGenderDropdown, setGender,
                )}
              </View>
            </View>

            {/* Section 2: Profile */}
            <View style={sectionStyles.container}>
              <Text style={sectionStyles.title}>
                {isRTL ? 'الملف التعريفي' : 'Profile'}
              </Text>

              {/* Row 1: المدرسة + الفئة العمرية */}
              <View style={sectionStyles.row}>
                {renderDropdown(
                  isRTL ? 'الفئة العمرية' : 'Age Group', ageGroup, ageGroupLabel,
                  isRTL ? 'اختر الفئة العمرية' : 'Select age group',
                  ageGroupOptions, showAgeGroupDropdown, setShowAgeGroupDropdown, setAgeGroup,
                )}
                {renderDropdown(
                  isRTL ? 'المدرسة' : 'School', school, schoolLabel,
                  isRTL ? 'اختر المدرسة' : 'Select school',
                  schoolOptions, showSchoolDropdown, setShowSchoolDropdown, setSchool,
                )}
              </View>

              {/* Row 2: المستوى + المواهب */}
              <View style={sectionStyles.row}>
                <FormField label={isRTL ? 'المواهب' : 'Specializations'} required>
                  <TouchableOpacity
                    style={[inputStyles.container, { flexDirection: 'row', alignItems: 'center' }, specializationIdParam ? { opacity: 0.7 } : undefined]}
                    onPress={() => !specializationIdParam && setShowSpecDropdown(!showSpecDropdown)}
                    activeOpacity={specializationIdParam ? 1 : 0.7}
                  >
                    <ChevronDownIcon size={20} color={MUTED} />
                    <View style={{ flex: 1, flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 4 }}>
                      {enrolledSpecs.length > 0 ? (
                        enrolledSpecs.map(spec => (
                          <View key={spec.id} style={chipStyles.chip}>
                            {!specializationIdParam && (
                              <TouchableOpacity onPress={() => {
                                const remaining = enrolledSpecs.filter(s => s.id !== spec.id);
                                setEnrolledSpecs(remaining);
                              }}>
                                <XIcon size={12} color={FOREGROUND} />
                              </TouchableOpacity>
                            )}
                            <Text style={chipStyles.chipText}>{spec.name}</Text>
                          </View>
                        ))
                      ) : (
                        <Text style={{ fontFamily: FONTS.regular, fontSize: 14, color: '#94A3B8', textAlign: 'right' }}>
                          {isRTL ? 'اختر الموهبة' : 'Select specialization'}
                        </Text>
                      )}
                    </View>
                  </TouchableOpacity>
                  {showSpecDropdown && (
                    <View style={dropdownStyles.menu}>
                      <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                        {(isCreateMode ? specOptions : allEnrolledSpecs).filter(opt => !enrolledSpecs.some(s => s.id === opt.id)).map((opt) => (
                          <TouchableOpacity
                            key={opt.id}
                            style={[dropdownStyles.item, enrolledSpecs.some(s => s.id === opt.id) && { backgroundColor: '#F1F5F9' }]}
                            onPress={() => {
                              setEnrolledSpecs(prev => [...prev, { id: opt.id, name: opt.name }]);
                              setSpecializationFkId(opt.id);
                              setSpecializationFkName(opt.name);
                              setShowSpecDropdown(false);
                            }}
                          >
                            <Text style={dropdownStyles.itemText}>{opt.name}</Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>
                    </View>
                  )}
                </FormField>
                {renderDropdown(
                  isRTL ? 'المستوى' : 'Level', level, levelLabel,
                  isRTL ? 'اختر المستوى' : 'Select level',
                  levelOptions, showLevelDropdown, setShowLevelDropdown, setLevel,
                )}
              </View>

              {/* Row 3: الحالة (right side only) */}
              <View style={sectionStyles.row}>
                <View style={{ flex: 1 }} />
                {renderDropdown(
                  isRTL ? 'الحالة' : 'Status', status, statusLabel,
                  isRTL ? 'اختر الحالة' : 'Select status',
                  statusOptions, showStatusDropdown, setShowStatusDropdown, setStatus,
                  true,
                )}
              </View>
            </View>

            {/* Footer: Save + Cancel */}
            <View style={footerStyles.container}>
              <TouchableOpacity
                style={[footerStyles.saveBtn, saving && { opacity: 0.6 }]}
                onPress={handleSave}
                disabled={saving}
              >
                <Text style={footerStyles.saveBtnText}>
                  {saving
                    ? (isRTL ? '\u062C\u0627\u0631\u064A \u0627\u0644\u062D\u0641\u0638...' : 'Saving...')
                    : (isRTL ? '\u062D\u0641\u0638' : 'Save')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[footerStyles.cancelBtn, createShadow(0, 1, 3, '#000', 0.1)]}
                onPress={() => navigation.goBack()}
              >
                <Text style={footerStyles.cancelBtnText}>
                  {isRTL ? '\u0625\u0644\u063A\u0627\u0621' : 'Cancel'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </ScrollView>
      <Toast visible={!!toastMsg} message={toastMsg} type={toastType} onHide={() => { setToastMsg(''); setToastType('info'); }} />
    </PageContainer>
  );
}

/* ================================================================ */
/*  Styles                                                           */
/* ================================================================ */

const cardStyles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    padding: 20,
    gap: 20,
  },
  title: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
  },
});

const sectionStyles = StyleSheet.create({
  container: {
    backgroundColor: SECTION_BG,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 4,
    padding: 24,
    gap: 24,
  },
  title: {
    fontSize: 16,
    fontFamily: FONTS.bold || FONTS.medium,
    color: FOREGROUND,
    textAlign: 'right',
  },
  row: {
    flexDirection: 'row',
    gap: 24,
  },
});

const fieldStyles = StyleSheet.create({
  container: {
    flex: 1,
    gap: 6,
  },
  label: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
  },
});

const inputStyles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 7.5,
    minHeight: 36,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  input: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
    padding: 0,
    margin: 0,
  },
});

const dropdownStyles = StyleSheet.create({
  menu: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    marginTop: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 4,
    zIndex: 10,
  },
  item: {
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  itemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
  },
});

const chipStyles = StyleSheet.create({
  chip: {
    backgroundColor: SECTION_BG,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 4,
    borderRadius: 2,
  },
  chipText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
  },
});

const footerStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
    paddingTop: 16,
  },
  saveBtn: {
    backgroundColor: BRAND_RED,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 7.5,
    minHeight: 36,
    justifyContent: 'center',
    alignItems: 'center',
  },
  saveBtnText: {
    fontSize: 14,
    fontFamily: FONTS.bold || FONTS.medium,
    color: '#F8FAFC',
  },
  cancelBtn: {
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 7.5,
    minHeight: 36,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cancelBtnText: {
    fontSize: 14,
    fontFamily: FONTS.bold || FONTS.medium,
    color: FOREGROUND,
  },
});
